import React, { useEffect, useState } from 'react';
import { Modal, Row, Col, Form, Button, Alert, Card } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { createClient, getFaxTemplates } from './CustomerView.actions';
import Spinner from 'components/Spinner';
import { showNotification } from 'services/NotificationService';
import { formatPhoneNumber } from 'services/UtilityService';

const CreateClientModal = ({ show, onHide, customerInfoUuid, customerName, onSuccess }) => {
  const dispatch = useDispatch();

  const {
    creatingClient,
    createClientError,
    faxTemplates,
    loadingFaxTemplates,
  } = useSelector((state) => state.customerView);

  const [formState, setFormState] = useState({
    clientName: '',
    programName: '',
    clientNameAbbreviation: '',
    logoUrl: '',
    supportNumber: '',
    faxNumberInbound: '',
    isEmailNotificationEnabled: false,
    isMemberEngagementEnabled: false,
    hasCustomSwitches: false,
    isPrescriberEmailEnabled: false,
    isRxAdvocateEnabled: false,
    prescriberCallActivitiesEnabled: false,
    pharmacyCallActivitiesEnabled: false,
    useNvpSourcePricing: false,
    excludeStarSwitches: false,
    excludeStarDeprescriptions: false,
    populationType: 'medicare',
    language: 'ENGLISH',
    faxTemplate: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show) {
      dispatch(getFaxTemplates());
    }
  }, [dispatch, show]);

  useEffect(() => {
    if (faxTemplates && !formState.faxTemplate) {
      // Find the default template
      const defaultTemplateKey = Object.keys(faxTemplates).find(
        key => faxTemplates[key]?.default === true
      );
      if (defaultTemplateKey) {
        setFormState(prevState => ({
          ...prevState,
          faxTemplate: defaultTemplateKey
        }));
      }
    }
  }, [faxTemplates]);

  const validateForm = () => {
    const newErrors = {};
    if (!formState.clientName) newErrors.clientName = 'Client Name is required.';
    if (!formState.programName) newErrors.programName = 'Program Name is required.';
    if (!formState.clientNameAbbreviation) newErrors.clientNameAbbreviation = 'Client Abbreviation is required.';
    if (!formState.faxTemplate) newErrors.faxTemplate = 'Fax Template is required.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    let newValue = value;

    if (['supportNumber', 'faxNumberInbound'].includes(name)) {
      // Only allow digits
      newValue = value.replace(/[^0-9]/g, '').slice(0, 10);
    }

    setFormState(prevState => ({
      ...prevState,
      [name]: newValue
    }));
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setFormState(prevState => ({
      ...prevState,
      [name]: checked
    }));
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }
    dispatch(createClient(customerInfoUuid, formState))
      .then((result) => {
        if (result.type === 'CREATE_CLIENT_SUCCESS') {
        showNotification({
                    title: `Client created successfully!`,
                    message: 'The client for this customer is created successfully',
                    position: 'tr',
                    type: "success"
                });
          onSuccess();
          handleClose();
        } else {
            showNotification({
                title: 'Error creating client.',
                position: 'tr',
                type: "error"
            });
        }
      });
  };

  const handleClose = () => {
    setFormState({
        clientName: '', programName: '', clientNameAbbreviation: '', logoUrl: '',
        supportNumber: '', faxNumberInbound: '',
        isEmailNotificationEnabled: false,
        isMemberEngagementEnabled: false, hasCustomSwitches: false, language: 'ENGLISH', faxTemplate: '', populationType: 'medicare', isPrescriberEmailEnabled: false, isRxAdvocateEnabled: false,
        prescriberCallActivitiesEnabled: false,
        pharmacyCallActivitiesEnabled: false,
        useNvpSourcePricing: false,
        excludeStarSwitches: false,
        excludeStarDeprescriptions: false,
    });
    setErrors({});
    dispatch({ type: 'CREATE_CLIENT_FAILURE', payload: null });
    onHide();
  }

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create Client for {customerName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadingFaxTemplates ? <Spinner /> : (
          <>
            {createClientError && <Alert variant="danger">An error occurred while creating the client.</Alert>}
            <Form>
              <Card className="mb-3">
                <Card.Header><Card.Title as="h6">Identity</Card.Title></Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}><Form.Group><Form.Label>Client Name <span className="text-danger">*</span></Form.Label><Form.Control type="text" name="clientName" value={formState.clientName} onChange={handleInputChange} isInvalid={!!errors.clientName} /><Form.Control.Feedback type="invalid">{errors.clientName}</Form.Control.Feedback></Form.Group></Col>
                    <Col md={6}><Form.Group><Form.Label>Program Name <span className="text-danger">*</span></Form.Label><Form.Control type="text" name="programName" value={formState.programName} onChange={handleInputChange} isInvalid={!!errors.programName} /><Form.Control.Feedback type="invalid">{errors.programName}</Form.Control.Feedback></Form.Group></Col>
                    <Col md={6} className="mt-2"><Form.Group><Form.Label>Client Abbreviation <span className="text-danger">*</span></Form.Label><Form.Control type="text" name="clientNameAbbreviation" value={formState.clientNameAbbreviation} onChange={handleInputChange} isInvalid={!!errors.clientNameAbbreviation} /><Form.Control.Feedback type="invalid">{errors.clientNameAbbreviation}</Form.Control.Feedback></Form.Group></Col>
                    <Col md={6} className="mt-2"><Form.Group><Form.Label>Logo URL</Form.Label><Form.Control type="text" name="logoUrl" value={formState.logoUrl} onChange={handleInputChange} /></Form.Group></Col>
                  </Row>
                </Card.Body>
              </Card>
              <Card className="mb-3">
                <Card.Header><Card.Title as="h6">Contact</Card.Title></Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}><Form.Group><Form.Label>Support Number</Form.Label><Form.Control type="text" name="supportNumber" value={formatPhoneNumber(formState.supportNumber)} onChange={handleInputChange} placeholder="e.g. (555) 555-5555" maxLength={14} /></Form.Group></Col>
                    <Col md={6}><Form.Group><Form.Label>Inbound Fax Number</Form.Label><Form.Control type="text" name="faxNumberInbound" value={formatPhoneNumber(formState.faxNumberInbound)} onChange={handleInputChange} placeholder="e.g. (555) 555-5555" maxLength={14} /></Form.Group></Col>
                  </Row>
                </Card.Body>
              </Card>
              <Card className="mb-3">
                <Card.Header><Card.Title as="h6">Call Activities</Card.Title></Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}><Form.Group><Form.Check type="switch" id="prescriber-call-activities-switch" label="Prescribers" name="prescriberCallActivitiesEnabled" checked={formState.prescriberCallActivitiesEnabled} onChange={handleCheckboxChange} disabled /></Form.Group></Col>
                    <Col md={6}><Form.Group><Form.Check type="switch" id="pharmacy-call-activities-switch" label="Pharmacies" name="pharmacyCallActivitiesEnabled" checked={formState.pharmacyCallActivitiesEnabled} onChange={handleCheckboxChange} disabled /></Form.Group></Col>
                  </Row>
                </Card.Body>
              </Card>
              <Card className="mb-3">
                <Card.Header><Card.Title as="h6">Other</Card.Title></Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}><Form.Group><Form.Check type="switch" id="custom-switches-switch" label="Custom Switches" name="hasCustomSwitches" checked={formState.hasCustomSwitches} onChange={handleCheckboxChange} /></Form.Group></Col>
                    <Col md={6}><Form.Group><Form.Check type="switch" id="prescriber-email-switch" label="Prescriber Emails" name="isPrescriberEmailEnabled" checked={formState.isPrescriberEmailEnabled} onChange={handleCheckboxChange} /></Form.Group></Col>
                  </Row>
                  <Row className="mt-2">
                    <Col md={6}><Form.Group><Form.Check type="switch" id="rx-advocate-enabled-switch" label="RX Advocate" name="isRxAdvocateEnabled" checked={formState.isRxAdvocateEnabled} onChange={handleCheckboxChange} /></Form.Group></Col>
                  </Row>
                </Card.Body>
              </Card>
              <Card className="mb-3">
                <Card.Header><Card.Title as="h6">Pricing</Card.Title></Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}><Form.Group><Form.Check type="switch" id="use-nvp-source-pricing-switch" label="Use Price Factors" name="useNvpSourcePricing" checked={formState.useNvpSourcePricing} onChange={handleCheckboxChange} /></Form.Group></Col>
                  </Row>
                </Card.Body>
              </Card>
              <Card className="mb-3">
                <Card.Header><Card.Title as="h6">STARS</Card.Title></Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}><Form.Group><Form.Check type="switch" id="exclude-star-switches-switch" label="Exclude STARS Medications" name="excludeStarSwitches" checked={formState.excludeStarSwitches} onChange={handleCheckboxChange} /></Form.Group></Col>
                    <Col md={6}><Form.Group><Form.Check type="switch" id="exclude-star-deprescriptions-switch" label="Hide Deprescribe Suggestions" name="excludeStarDeprescriptions" checked={formState.excludeStarDeprescriptions} onChange={handleCheckboxChange} /></Form.Group></Col>
                  </Row>
                </Card.Body>
              </Card>
              <Card className="mb-3">
                <Card.Header><Card.Title as="h6">Notifications</Card.Title></Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}><Form.Group><Form.Check type="switch" id="email-notification-switch" label="Email Notification" name="isEmailNotificationEnabled" checked={formState.isEmailNotificationEnabled} onChange={handleCheckboxChange} /></Form.Group></Col>
                    <Col md={6}><Form.Group><Form.Check type="switch" id="member-engagement-switch" label="Member Engagement" name="isMemberEngagementEnabled" checked={formState.isMemberEngagementEnabled} onChange={handleCheckboxChange} /></Form.Group></Col>
                  </Row>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header><Card.Title as="h6">Program defaults</Card.Title></Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Language</Form.Label>
                        <Form.Control as="select" name="language" value={formState.language} onChange={handleInputChange}>
                          <option value="ENGLISH">English</option>
                          <option value="SPANISH">Spanish</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Fax Template <span className="text-danger">*</span></Form.Label>
                        <Form.Control as="select" name="faxTemplate" value={formState.faxTemplate} onChange={handleInputChange} isInvalid={!!errors.faxTemplate}>
                          <option value="">Select a template</option>
                          {faxTemplates && typeof faxTemplates === 'object' && !Array.isArray(faxTemplates) && Object.keys(faxTemplates).length > 0
                            ? Object.entries(faxTemplates).map(([key, template]) => {
                                const displayName = typeof template === 'object' && template !== null && template.displayName
                                  ? template.displayName
                                  : typeof template === 'string'
                                  ? template
                                  : key;
                                const isDisabled = typeof template === 'object' && template !== null && template.disabled === true;
                                const displayText = isDisabled ? `${displayName} - Deprecated` : displayName;
                                return (
                                  <option key={key} value={key} disabled={isDisabled}>
                                    {displayText}
                                  </option>
                                );
                              })
                            : !loadingFaxTemplates && (
                                <option value="" disabled>No templates available</option>
                              )}
                        </Form.Control>
                        <Form.Control.Feedback type="invalid">{errors.faxTemplate}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mt-2">
                      <Form.Group>
                        <Form.Label>Population Type</Form.Label>
                        <Form.Control as="select" name="populationType" value={formState.populationType} onChange={handleInputChange}>
                          <option value="medicare">Medicare</option>
                          <option value="medicaid">Medicaid</option>
                          <option value="commercial">Commercial</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Form>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={creatingClient}>
          {creatingClient ? 'Creating...' : 'Create Client'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateClientModal;